/**
 * @fileoverview Aba "Ordens de serviço" do editor de triagem: começar a
 * triagem pelas OS que chegam de uma fonte (ex.: portal ICS da Midea).
 * Automático: a IA procura o cliente assim que a OS chega (ou muda) para um
 * dos estados escolhidos, dentro do horário de envio. Manual: só pelo botão
 * na tela "Ordens de serviço". A conversa abre pela conexão do Contato ativo.
 */

import React from "react";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import Checkbox from "@material-ui/core/Checkbox";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import { i18n } from "../../../../translate/i18n";
import ModernSelect from "../../../../components/ModernSelect";
import useStyles from "./styles";
import SendWindowFields from "./SendWindowFields";

const w = (key, params) => i18n.t(`triage.manager.editor.workOrders.${key}`, params);

const alternar = (lista, item) => (lista.includes(item) ? lista.filter((x) => x !== item) : [...lista, item]);

const WorkOrdersEditor = ({ value, onChange, sources, outreachConnection, hasTemplate, onGoToContact }) => {
  const classes = useStyles();
  const fonte = sources.find((s) => String(s.id) === String(value.sourceId)) || null;
  const set = (patch) => onChange({ ...value, ...patch });

  // Fonte escolhida pela primeira vez: estados e o tipo "conserto" já marcados.
  const escolherFonte = (id) => {
    const nova = sources.find((s) => String(s.id) === String(id));
    const statuses = value.statuses.length ? value.statuses : (nova?.statuses || []).map((s) => s.code);
    const conserto = (nova?.types || []).find((t) => t.code === "Repair");
    const types = value.types.length ? value.types : conserto ? [conserto.code] : [];
    set({ sourceId: id, statuses, types });
  };

  const auto = value.startMode === "auto";
  const metaSemTemplate = outreachConnection?.provider === "meta-api" && !hasTemplate;

  return (
    <div className={classes.form}>
      <div className={classes.hint}>{w("intro")}</div>

      <FormControlLabel
        className={classes.check}
        control={<Switch color="primary" checked={value.enabled} onChange={(ev) => set({ enabled: ev.target.checked })} />}
        label={w("enabled")}
      />

      {value.enabled && sources.length === 0 && <div className={classes.warnText}>{w("noSources")}</div>}

      {value.enabled && sources.length > 0 && (
        <>
          <ModernSelect
            className={classes.field}
            size="small"
            fullWidth
            label={w("source")}
            InputLabelProps={{ shrink: true }}
            displayEmpty
            value={value.sourceId}
            onChange={(ev) => escolherFonte(ev.target.value)}
          >
            <MenuItem value="">
              <em>{w("sourceNone")}</em>
            </MenuItem>
            {sources.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.active ? s.name : `${s.name} · ${w("inactive")}`}
              </MenuItem>
            ))}
          </ModernSelect>

          <div>
            <div className={classes.label}>{w("startMode")}</div>
            <RadioGroup className={classes.radioGroup} value={value.startMode} onChange={(ev) => set({ startMode: ev.target.value })}>
              {["auto", "manual"].map((m) => (
                <FormControlLabel key={m} value={m} control={<Radio size="small" color="primary" />} label={w(`modes.${m}`)} />
              ))}
            </RadioGroup>
            <div className={classes.hint}>{auto ? w("autoHint") : w("manualHint")}</div>
          </div>

          {fonte && (
            <>
              <div>
                <div className={classes.label}>{w("statuses")}</div>
                <div className={classes.hint}>{w("statusesHint")}</div>
                <div className={classes.optionGrid}>
                  {fonte.statuses.map((s) => (
                    <FormControlLabel
                      key={s.code}
                      className={classes.check}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={value.statuses.includes(s.code)}
                          onChange={() => set({ statuses: alternar(value.statuses, s.code) })}
                        />
                      }
                      label={s.label}
                    />
                  ))}
                </div>
              </div>

              <div>
                <div className={classes.label}>{w("types")}</div>
                <div className={classes.hint}>{w("typesHint")}</div>
                <div className={classes.optionGrid}>
                  {fonte.types.map((t) => (
                    <FormControlLabel
                      key={t.code}
                      className={classes.check}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={value.types.includes(t.code)}
                          onChange={() => set({ types: alternar(value.types, t.code) })}
                        />
                      }
                      label={t.label}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {auto && <SendWindowFields title={w("window")} hint={w("windowHint")} value={value} onChange={set} />}

          <div className={classes.hint}>
            {w("contactName")}{" "}
            {onGoToContact && (
              <button type="button" className={classes.link} onClick={onGoToContact}>
                {w("contactNameOpen")}
              </button>
            )}
          </div>

          <div className={classes.noteBox}>
            <div>{w("connectionNote")}</div>
            {!outreachConnection && <div className={classes.warnText}>{w("noConnection")}</div>}
            {metaSemTemplate && <div className={classes.warnText}>{w("metaNeedsTemplate")}</div>}
            {auto && <div>{w("autoSinceNote")}</div>}
            <div>{w("humanNote")}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default WorkOrdersEditor;
